<template>
    <article>
        <SmartHeadline v-if="content.headline"
                       :level="isFirstBlock ? 'h1' : 'h2'"
                       :style-as-h1="isFirstBlock"
                       class="mb-10">
            {{ content.headline }}
        </SmartHeadline>
        <section>
            <iframe
                ref="jotform"
                :title="content.headline"
                :allowtransparency="content.allowTransparency"
                :allowfullscreen="content.allowFullscreen"
                :scrolling="content.allowScrolling ? 'yes' : 'no'"
                allow="geolocation; microphone; camera"
                :src="jotformSrc"
                frameborder="0"
                style="width: 1px; min-width: 100%; border:none;">
            </iframe>
        </section>
    </article>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, PropType } from 'vue';
import { JotFormBlockViewModel } from '@/types/contentServerContract';
import SmartHeadline from '@/core/ui-components/SmartHeadline.vue';

const props = defineProps({
    content: {
        type: Object as PropType<JotFormBlockViewModel>,
        required: true
    },
    isFirstBlock: {
        type: Boolean,
        required: false,
        default: false
    }
});

const jotform = ref();

const jotformSrc = computed(() => {
    return `https://form.jotform.com/${props.content.jotFormId}`;
});

const handleIFrameMessage = (e: any) => {
    if (typeof e.data === 'object') return;

    const iframe = jotform.value;
    if (!iframe) return;

    const args = e.data.split(':');
    const _window = window as any;

    switch (args[0]) {
        case 'scrollIntoView':
            if (props.isFirstBlock) iframe.scrollIntoView();
            break;
        case 'setHeight':
            iframe.style.height = `${args[1]}px`;
            break;
        case 'collapseErrorPage':
            if (iframe.clientHeight > window.innerHeight) {
                iframe.style.height = `${window.innerHeight}px`;
            }
            break;
        case 'loadScript': {
            let src = args[1];
            if (args.length > 3) {
                src = args[1] + ':' + args[2];
            }
            const script = document.createElement('script');
            script.src = src;
            script.type = 'text/javascript';
            document.body.appendChild(script);
            break;
        }
        case 'exitFullscreen':
            if (_window.document.exitFullscreen) _window.document.exitFullscreen();
            else if (_window.document.mozCancelFullScreen) _window.document.mozCancelFullScreen();
            else if (_window.document.mozCancelFullscreen) _window.document.mozCancelFullscreen();
            else if (_window.document.webkitExitFullscreen) _window.document.webkitExitFullscreen();
            else if (_window.document.msExitFullscreen) _window.document.msExitFullscreen();
            break;
    }

    const isJotForm = (e.origin.indexOf('jotform') > -1);
    if (isJotForm && 'contentWindow' in iframe && 'postMessage' in iframe.contentWindow) {
        const urls = {
            docurl: encodeURIComponent(document.URL),
            referrer: encodeURIComponent(document.referrer)
        };
        iframe.contentWindow.postMessage(JSON.stringify({ type: 'urls', value: urls }), '*');
    }
};

onMounted(() => {
    window.addEventListener('message', handleIFrameMessage, false);
});

onBeforeUnmount(() => {
    window.removeEventListener('message', handleIFrameMessage);
});
</script>
